import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'

// Components & Services
import UpdateToolIdModal from './UpdateToolIdModal'
import toolsInfoService from '../../service/toolsInfo.service'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import { useAdminGlobalStateContext } from '../../context/AdminGlobalStateContext'

// Styled components
const KPITable = styled('table')(() => ({
  borderCollapse: 'collapse',
  width: '100%'
}))

const KPITableRow = styled('tr')(() => ({
  width: '100%',
  '&:nth-of-type(even)': {
    backgroundColor: '#f9f9f9'
  }
}))

const KPITableHeading = styled('th')(() => ({
  border: '1px solid #dddddd',
  textAlign: 'center',
  padding: '8px'
}))

const KPITableEntry = styled('td')(({ alignment }) => ({
  border: '1px solid #dddddd',
  textAlign: 'center',
  padding: '8px'
}))

const MachinesToolInfo = () => {
  const [refetchCount, setRefetchCount] = useState(0)
  const [toolsInfo, setToolsInfo] = useState([])
  const [toolIdUpdateModalInfo, setToolIdUpdateModalInfo] = useState(null)
  const [machine, setMachine] = useState(null)

  const { machines: machineOptions } = useAdminGlobalStateContext()

  // Fetch tools info
  useEffect(() => {
    const fetchToolsInfo = async () => {
      try {
        const toolsInfo = await toolsInfoService.getToolsInfo()
        setToolsInfo(toolsInfo.data)
      } catch (error) {
        console.error('Error fetching tool info:', error)
      }
    }
    fetchToolsInfo()
  }, [refetchCount])

  useEffect(() => {
    if (machineOptions.length > 0) {
      setMachine(machineOptions[0])
    }
  }, [machineOptions])

  const filteredToolsInfo = machine
    ? toolsInfo
        .filter((item) => item.machine === machine.value)
        .reduce((acc, curr) => {
          const parsedTools = Array.isArray(curr.toolsInfo)
            ? curr.toolsInfo
            : JSON.parse(curr.toolsInfo)

          const existing = acc.find((m) => m.machine === curr.machine)

          if (existing) {
            existing.toolsInfo = [...existing.toolsInfo, ...parsedTools]
          } else {
            acc.push({
              ...curr,
              toolsInfo: [...parsedTools]
            })
          }
          return acc
        }, [])
    : []

  const unit1Machines = ['BALABHEEMA', 'BALARAMA']

  // NOTE: This filter is applied to show machines for which tool life is monitored
  const machineOptionsWithMonitoring =
    process.env.REACT_APP_UNIT === 'unit1'
      ? machineOptions.filter((item) => unit1Machines.includes(item.value))
      : machineOptions

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee'
        }}
      >
        <div style={{ marginLeft: 'auto', width: '200px' }}>
          <StyledSelectField
            label=""
            placeHolderText="Select machine"
            selectedValue={machine}
            selectOptions={machineOptionsWithMonitoring}
            onChangeHandler={(value) => setMachine(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={false}
            noOptionsMessage="No machine found"
          />
        </div>
      </Stack>

      <Box
        sx={{ padding: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}
      >
        {filteredToolsInfo.map((rowItem) => (
          <Box
            key={rowItem.machine}
            sx={{
              width: '100%',
              border: '1px solid grey',
              borderRadius: '8px',
              padding: '16px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <Box>Machine: {rowItem.machine}</Box>
            <KPITable>
              <KPITableRow>
                <KPITableHeading>Tool</KPITableHeading>
                <KPITableHeading>Active</KPITableHeading>
                <KPITableHeading>Tool type</KPITableHeading>
                <KPITableHeading>Tool ID</KPITableHeading>
                <KPITableHeading>Changed at</KPITableHeading>
                <KPITableHeading>Action</KPITableHeading>
              </KPITableRow>

              {rowItem.toolsInfo.map((toolItem, index) => (
                <KPITableRow key={index}>
                  <KPITableEntry>Tool {toolItem.toolPosition}</KPITableEntry>
                  <KPITableEntry alignment="center">
                    <Checkbox
                      disabled
                      sx={{ padding: 0, zIndex: 5 }}
                      checked={toolItem.isActive}
                    />
                  </KPITableEntry>
                  <KPITableEntry>{toolItem.toolType}</KPITableEntry>
                  <KPITableEntry>{toolItem.toolId}</KPITableEntry>
                  <KPITableEntry>{toolItem.changedAt}</KPITableEntry>
                  <KPITableEntry alignment="center">
                    <BuildCircleOutlinedIcon
                      onClick={() =>
                        setToolIdUpdateModalInfo({
                          toolPosition: toolItem.toolPosition,
                          isActive: toolItem.isActive,
                          toolType: toolItem.toolType,
                          toolTypeId: toolItem.toolTypeId,
                          toolId: toolItem.toolId,
                          machine: rowItem.machine
                        })
                      }
                      fontSize="small"
                      sx={{ cursor: 'pointer' }}
                    />
                  </KPITableEntry>
                </KPITableRow>
              ))}
            </KPITable>
          </Box>
        ))}

        {toolIdUpdateModalInfo && (
          <UpdateToolIdModal
            isModalOpen={toolIdUpdateModalInfo}
            machine={toolIdUpdateModalInfo.machine}
            toolPosition={toolIdUpdateModalInfo.toolPosition}
            isActive={toolIdUpdateModalInfo.isActive}
            toolId={toolIdUpdateModalInfo.toolId}
            toolType={toolIdUpdateModalInfo.toolType}
            toolTypeId={toolIdUpdateModalInfo.toolTypeId}
            closeHandler={() => setToolIdUpdateModalInfo(null)}
            submitHandler={async (
              machine,
              toolPosition,
              toolType,
              toolId,
              isActive
            ) => {
              try {
                await toolsInfoService.updateToolsInfo(machine, {
                  toolPosition,
                  toolType,
                  toolId,
                  isActive
                })
                setToolIdUpdateModalInfo(null)
                setRefetchCount((prev) => prev + 1)
              } catch (error) {
                console.error('Error updating tool info:', error)
              }
            }}
          />
        )}
      </Box>
    </>
  )
}

export default MachinesToolInfo
